/**
 * The records the examiner read for a line, as chips under it: a finding by
 * its severity, anything else by its kind's icon, then a short title. The
 * best few show and the rest fold into "+N". Beside a desk a chip opens its
 * record's row there (`OpenRowsProvider`), which the page brings into view
 * and flashes, and stays pressed while its row is open; with no desk beside
 * it (`orgSegment` given), a chip opens the record itself in a new tab.
 *
 * @module components/apps/probe/ProbeRecordChips
 */
import { GlobeIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { WebsiteToolIcon } from "../../chat/WebsiteToolIcon";
import { canFold, foldSources } from "../../regulation/probe/ProbeSources.logic";
import { KIND_LABEL, useOpenRows } from "./desk/DeskParts";
import type { DeskRecord } from "./desk/deskRecords";
import { DESK_LABEL_CLASS, KIND_ICON, RecordLink } from "./ProbeDesk";
import { SeverityGlyph } from "./ProbeParts";

const CHIP_CLASS =
  "inline-flex h-6 max-w-60 cursor-pointer items-center gap-1.5 rounded-full border border-border/60 bg-background px-2 text-xs text-muted-foreground outline-none transition-colors hover:border-border hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/60 dark:text-foreground/70";

/** A chip whose record's row is open on the desk: pressed in, edged in the colour its row flashes. */
const OPEN_CHIP_CLASS =
  "border-foreground/50 bg-accent text-foreground hover:border-foreground/60 dark:text-foreground";

function ChipIcon({ record }: { readonly record: DeskRecord }) {
  if (record.kind === "finding" && record.severity !== null)
    return <SeverityGlyph severity={record.severity} className="-ms-0.5 size-3.5" />;
  if (record.kind === "web" && record.link?.kind === "url")
    return (
      <WebsiteToolIcon
        pageUrl={record.link.url}
        fallback={<GlobeIcon aria-hidden className="size-3.5 shrink-0" />}
      />
    );
  const Icon = KIND_ICON[record.kind];
  return <Icon aria-hidden className={cn("size-3.5 shrink-0", DESK_LABEL_CLASS)} />;
}

export function ProbeRecordChips({
  records,
  orgSegment,
  className,
}: {
  readonly records: ReadonlyArray<DeskRecord>;
  /** No desk beside the chips: each opens its record's own page, in this organization. */
  readonly orgSegment?: string;
  readonly className?: string;
}) {
  const rows = useOpenRows();
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = foldSources(records, expanded);
  return (
    <ul
      className={cn("mt-2 flex flex-wrap gap-1.5", className)}
      aria-label="What the examiner read"
      data-probe-chips=""
    >
      {shown.map((record) => {
        const open = rows?.open === record.key;
        if (orgSegment !== undefined)
          return (
            <li key={record.key} className="flex min-w-0">
              <RecordLink
                link={record.link}
                orgSegment={orgSegment}
                className={CHIP_CLASS}
              >
                <ChipIcon record={record} />
                <span className="sr-only">{KIND_LABEL[record.kind].one}: </span>
                <span className="truncate" title={record.title}>
                  {record.title}
                </span>
              </RecordLink>
            </li>
          );
        return (
          <li key={record.key} className="flex min-w-0">
            <button
              type="button"
              className={cn(CHIP_CLASS, open && OPEN_CHIP_CLASS)}
              aria-expanded={open}
              title={`${KIND_LABEL[record.kind].one}: ${record.title}`}
              onClick={() => rows?.show(record.key)}
              data-probe-chip={record.key}
            >
              <ChipIcon record={record} />
              {record.kind === "finding" ? null : (
                <span className="sr-only">{KIND_LABEL[record.kind].one}: </span>
              )}
              <span className="truncate">{record.title}</span>
            </button>
          </li>
        );
      })}
      {canFold(records) ? (
        <li className="flex">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className={cn(CHIP_CLASS, "tabular-nums")}
            data-probe-chips-more=""
          >
            {expanded ? "Fewer" : `+${hidden}`}
          </button>
        </li>
      ) : null}
    </ul>
  );
}
